import { useMemo } from "react";
import {
  Badge,
  MultiSelect,
  Select,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconPencil, IconTrash } from "@tabler/icons";
import _ from "lodash";
import ActionCellRender from "../../components/actionCellRender";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
  PageList,
} from "../../components/pageList";
import { PersonalTradingBrokerField } from "../../components/personalTradingAccountRow";
import RemoteSelect2 from "../../components/remoteSelect2";
import { McaApprovalListBadge } from "../../components/mca/McaEntityIndicators";
import {
  DATA_PERSONAL_TRADING_ACCOUNT_TYPE,
  DATA_PERSONAL_TRADING_CURRENCY,
  DATA_PERSONAL_TRADING_MARKET,
  DATA_PERSONAL_TRADING_PRODUCT_TYPE,
  DATA_PERSONAL_TRADING_RELATIONSHIP,
  DATA_PERSONAL_TRADING_STATUS,
} from "../../data/options";
import { useCellRender } from "../../hooks/useCellRender";

const apiEntity = "personalTradingAccount";

const optionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label || value || "-";

const consultantSnapshot = (consultant) => {
  if (!consultant) return {};
  return {
    name: consultant.name,
    cname: consultant.cname,
    staffId: consultant.staffId,
    email: consultant.email,
    aliasName: consultant.aliasName,
    _id: consultant._id,
  };
};

export const buildPersonalTradingAccountFormSchema = ({
  lockConsultant = false,
  consultant = null,
} = {}) => ({
  title: "Personal Trading Account",
  isMainForm: true,
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "xl",
  },
  closeAfterSave: true,
  apiEntity,
  initialValues: {
    consultant: consultant?._id || "",
    consultantInfo: consultantSnapshot(consultant),
    accountStatus: "ACTIVE",
    productTypes: [],
    markets: [],
  },
  validate: {
    consultant: (value) => (_.isEmpty(value) ? "Please select consultant" : null),
    brokerName: (value) => (!value ? "Please select a broker" : null),
  },
  layout: {
    containers: [
      {
        key: "main-g",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: [
          { key: "col-left", props: { span: 6 } },
          { key: "col-right", props: { span: 6 } },
        ],
      },
    ],
    fields: [
      {
        name: "consultant",
        component: RemoteSelect2,
        parent: "col-left",
        props: {
          label: "Consultant",
          apiEntity: "user",
          required: true,
          disabled: lockConsultant,
          placeholder: "Please select consultant",
          valueField: "_id",
          labelField: "name",
          labelRender: (data) =>
            `${data.name}${data.aliasName ? `, ${data.aliasName}` : ""} (${
              data.staffId ?? " -- "
            })`,
          onDataChange: (selected, form) => {
            form.setFieldValue("consultantInfo", consultantSnapshot(selected));
          },
          searchFields: ["aliasName", "name", "staffId", "cname", "email", "phone"],
          sort: { by: "name", order: "asc" },
        },
      },
      {
        name: "brokerName",
        component: PersonalTradingBrokerField,
        parent: "col-left",
        props: {
          required: true,
          label: "Broker Name",
          placeholder: "Select or type a broker",
        },
      },
      {
        name: "accountNumber",
        component: TextInput,
        parent: "col-left",
        props: { label: "Account Number" },
      },
      {
        name: "accountHolderName",
        component: TextInput,
        parent: "col-left",
        props: { label: "Account Holder" },
      },
      {
        name: "accountType",
        component: Select,
        parent: "col-left",
        props: {
          label: "Account Type",
          data: DATA_PERSONAL_TRADING_ACCOUNT_TYPE,
          searchable: true,
          clearable: true,
        },
      },
      {
        name: "relationship",
        component: Select,
        parent: "col-right",
        props: {
          label: "Relationship",
          data: DATA_PERSONAL_TRADING_RELATIONSHIP,
          searchable: true,
          clearable: true,
        },
      },
      {
        name: "accountStatus",
        component: Select,
        parent: "col-right",
        props: {
          label: "Account Status",
          data: DATA_PERSONAL_TRADING_STATUS,
          required: true,
        },
      },
      {
        name: "productTypes",
        component: MultiSelect,
        parent: "col-right",
        props: {
          label: "Product Types",
          data: DATA_PERSONAL_TRADING_PRODUCT_TYPE,
          searchable: true,
          clearable: true,
        },
      },
      {
        name: "markets",
        component: MultiSelect,
        parent: "col-right",
        props: {
          label: "Markets",
          data: DATA_PERSONAL_TRADING_MARKET,
          searchable: true,
          clearable: true,
        },
      },
      {
        name: "currency",
        component: Select,
        parent: "col-right",
        props: {
          label: "Currency",
          data: DATA_PERSONAL_TRADING_CURRENCY,
          searchable: true,
          clearable: true,
        },
      },
      {
        name: "openDate",
        component: DatePicker,
        parent: "col-left",
        props: {
          label: "Open Date",
          placeholder: "Pick Date",
          allowFreeInput: true,
          zIndex: 1000,
        },
      },
      {
        name: "closeDate",
        component: DatePicker,
        parent: "col-left",
        props: {
          label: "Close Date",
          placeholder: "Pick Date",
          allowFreeInput: true,
          zIndex: 1000,
        },
      },
      {
        name: "remark",
        component: Textarea,
        parent: "col-right",
        props: { label: "Remark", minRows: 2 },
      },
    ],
  },
});

export const buildPersonalTradingAccountColumns = ({
  cellRender,
  showConsultant = true,
} = {}) => {
  const columns = [];
  if (showConsultant) {
    columns.push({
      field: "consultant",
      headerName: "Consultant",
      sortable: true,
      cellRender: (col, data) =>
        data.consultant?.name
          ? cellRender.User(data.consultant)
          : data.consultantInfo?.name || "-",
    });
  }
  columns.push(
    {
      field: "brokerName",
      headerName: "Broker Name",
      sortable: true,
    },
    {
      field: "accountNumber",
      headerName: "Account Number",
    },
    {
      field: "accountType",
      headerName: "Account Type",
      cellRender: (col, data) =>
        optionLabel(DATA_PERSONAL_TRADING_ACCOUNT_TYPE, data.accountType),
    },
    {
      field: "accountStatus",
      headerName: "Status",
      cellRender: (col, data) =>
        optionLabel(DATA_PERSONAL_TRADING_STATUS, data.accountStatus),
    },
    {
      field: "productTypes",
      headerName: "Product Types",
      cellRender: (col, data) => {
        const values = data.productTypes?.length
          ? data.productTypes
          : data.productType
            ? [data.productType]
            : [];
        if (!values.length) return "-";
        return values
          .map((value) => optionLabel(DATA_PERSONAL_TRADING_PRODUCT_TYPE, value))
          .join(", ");
      },
    },
    {
      field: "status",
      headerName: "Approval",
      cellRender: (col, data) => (
        <>
          <Badge
            color={data.status === "APPROVED" ? "green" : "orange"}
            variant="light"
            mr={6}
          >
            {data.status === "APPROVED" ? "Approved" : "Draft"}
          </Badge>
          <McaApprovalListBadge entity={data} />
        </>
      ),
    },
    {
      field: "action",
      cellRender: ActionCellRender,
      isAction: true,
      actions: [{ name: "edit", icon: IconPencil, props: {} }],
      actionMenu: [
        {
          name: "delete",
          label: "Delete data",
          icon: IconTrash,
          props: { color: "red" },
        },
      ],
    }
  );
  return columns;
};

export const personalTradingSearchFields = [
  "brokerName",
  "accountNumber",
  "accountHolderName",
  "consultantInfo.name",
  "consultantInfo.cname",
  "consultantInfo.email",
  "consultantInfo.staffId",
  "consultantInfo.aliasName",
];

const PersonalTradingAccountPage = () => {
  const [cellRender] = useCellRender();
  const tableSchema = useMemo(
    () => ({
      showActionColumn: true,
      searchableFields: personalTradingSearchFields,
      columns: buildPersonalTradingAccountColumns({ cellRender }),
    }),
    [cellRender]
  );

  return (
    <PageList
      title="Personal Trading Accounts"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={buildPersonalTradingAccountFormSchema()}
    />
  );
};

export const UserPersonalTradingAccounts = ({ form }) => {
  const [cellRender] = useCellRender();
  const user = form?.values;
  const userId = user?._id;
  const consultant = useMemo(
    () =>
      userId
        ? {
            _id: userId,
            name: user?.name,
            cname: user?.cname,
            staffId: user?.staffId,
            email: user?.email,
            aliasName: user?.aliasName,
          }
        : null,
    [userId, user?.name, user?.cname, user?.staffId, user?.email, user?.aliasName]
  );

  const tableSchema = useMemo(
    () => ({
      name: `userPersonalTrading-${userId}`,
      preQuery: { consultant: userId },
      showActionColumn: true,
      searchableFields: ["brokerName", "accountNumber", "accountHolderName"],
      columns: buildPersonalTradingAccountColumns({
        cellRender,
        showConsultant: false,
      }),
    }),
    [cellRender, userId]
  );

  const formSchema = useMemo(
    () =>
      buildPersonalTradingAccountFormSchema({
        lockConsultant: true,
        consultant,
      }),
    [consultant]
  );

  if (!userId) {
    return (
      <Text size="sm" color="dimmed">
        Save the user before adding personal trading accounts.
      </Text>
    );
  }

  return (
    <PageList
      title=""
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      embedded
      hideSearchBar
    />
  );
};

export default PersonalTradingAccountPage;
